import React, { type FC, useState } from "react";
import { useFeatureFlags } from "@packsize/feature-flags";
import { FormattedMessage, useIntl } from "react-intl";
import {
	Card,
	CircularProgress,
	Grid,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
} from "@mui/material";
import { SearchTextField } from "@packsize/components";
import type { Role } from "@packsize/identity-api-sdk";

import { UsersAndRolesMoved } from "@/views/configuration/users-and-roles-moved";
import { ConfigurationPageLayout } from "@/views/configuration/components";
import { useRoles } from "@/hooks/roles";
import { DeleteRoleButton, EditRoleButton, NewRoleButton } from "@/views/configuration/roles/components";

export const Roles: FC = () => {
	const { isFeatureFlagEnabled } = useFeatureFlags();
	const intl = useIntl();
	const [searchTerm, setSearchTerm] = React.useState("");
	const { data: roles = [], isLoading } = useRoles();

	const [rowsPerPage, setRowsPerPage] = useState(50);
	const [page, setPage] = useState(0);

	const visibleRoles: Role[] = roles.filter((r: Role) => {
		const lowerFilter = searchTerm.toLowerCase();

		return r.name.toLowerCase().includes(lowerFilter);
	});

	const actions = (
		<>
			<SearchTextField
				value={searchTerm}
				onChange={setSearchTerm}
				placeholder={intl.formatMessage({ id: "Search roles...", defaultMessage: "Search roles..." })}
				size="small"
				data-testid="users__search__users"
				sx={{ backgroundColor: "common.white", width: "300px" }}
			/>
			<NewRoleButton />
		</>
	);

	if (isFeatureFlagEnabled("vaporware-company-link") === true) {
		return <UsersAndRolesMoved />;
	}

	return (
		<ConfigurationPageLayout title={intl.formatMessage({ id: "Roles", defaultMessage: "Roles" })} actions={actions}>
			<Card component={Grid} container direction="column" sx={{ flex: 1, overflow: "hidden" }}>
				<TableContainer sx={{ flex: 1 }}>
					<Table stickyHeader>
						<TableHead>
							<TableRow>
								<TableCell sx={{ width: "100%" }}>
									<FormattedMessage id="roles.table.header.name" defaultMessage="Name" />
								</TableCell>
								<TableCell align="center">
									<FormattedMessage id="roles.table.header.edit" defaultMessage="Edit" />
								</TableCell>
								<TableCell align="center">
									<FormattedMessage id="roles.table.header.delete" defaultMessage="Delete" />
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{isLoading === true && (
								<TableRow>
									<TableCell colSpan={3} align="center">
										<CircularProgress />
									</TableCell>
								</TableRow>
							)}
							{isLoading === false && roles.length === 0 && (
								<TableRow>
									<TableCell colSpan={3}>
										<FormattedMessage id="roles.table.empty" defaultMessage="No roles found" />
									</TableCell>
								</TableRow>
							)}
							{visibleRoles.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((r: Role) => (
								<TableRow key={r.id}>
									<TableCell data-testid={`roles-view__cell__name--${r.id}`}>{r.name}</TableCell>
									<TableCell align="center">
										<EditRoleButton readonly={false} role={r} />
									</TableCell>
									<TableCell align="center">
										<DeleteRoleButton role={r} />
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
				<TablePagination
					rowsPerPageOptions={[50, 100, 500]}
					component="div"
					count={roles.length}
					rowsPerPage={rowsPerPage}
					page={page}
					onPageChange={(_, pageNumber) => setPage(pageNumber)}
					onRowsPerPageChange={(event) => {
						setRowsPerPage(parseInt(event.target.value));
						setPage(0);
					}}
				/>
			</Card>
		</ConfigurationPageLayout>
	);
};
